import React from 'react';

import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import ModalStateType from 'models/generic/ModalState';
import InspectionTemplate from 'models/InspectionTemplateModel';
import Modal from 'components/common/Modal';
import axios from 'helpers/axios';
import config from 'helpers/config';
import InspectionTemplateTask from 'models/InspectionTemplateTaskModel';
import InspectionTemplateGroup from 'models/InspectionTemplateGroupModel';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { MdDragIndicator } from 'react-icons/md';
import { ChevronDown, ChevronUp, GripVertical, Info, Plus, Settings, Trash2, X } from 'lucide-react';
import Toggle from 'components/common/Toggle';

interface Props {
    inspection?: InspectionTemplate;
    onClose: (paymentCreated?: boolean, paymentUpdated?: boolean) => void;

    dispatch: Dispatch;
}

interface State {
    modalState: ModalStateType;
    modalHeader: string;

    inspection: InspectionTemplate;
    isLoading: boolean;
    isSubmitting: boolean;
}

class InspectionModal extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            modalState: !this.props.inspection ? ModalStateType.Create : ModalStateType.Edit,
            modalHeader: '',
            isLoading: false,
            inspection: {} as InspectionTemplate,
            isSubmitting: false,
        };

        this.handleChange = this.handleChange.bind(this);
        this.handleUpdate = this.handleUpdate.bind(this);
        this.addInspectionGroup = this.addInspectionGroup.bind(this);
        this.handleOnDragEnd = this.handleOnDragEnd.bind(this);
    }

    componentDidMount() {
        if (this.state.modalState === ModalStateType.Create) {
            this.setState({
                inspection: {
                    // inspectionTemplateItems: [{ description: '' }] as InspectionTemplateTask[],
                    groups: [
                        { description: '', isExpanded: true, tasks: [{ description: '' }] as InspectionTemplateTask[] },
                    ] as InspectionTemplateGroup[],
                } as unknown as InspectionTemplate,
            });
        } else {
            const inspectionCopy = this.props.inspection;
            // @ts-ignore
            inspectionCopy.inspectionTemplateItems = inspectionCopy.tasks;
            this.setState({ inspection: inspectionCopy! });
        }
        this.setModalHeader();
    }

    setModalHeader() {
        if (this.state.modalState === ModalStateType.Edit) {
            this.setState({ modalHeader: 'Edit Inspection' });
        } else {
            this.setState({ modalHeader: 'New Inspection' });
        }
    }

    updateInspectionTemplate(inspection: any) {
        inspection.tasks = inspection.inspectionTemplateItems;
        axios
            .put(`${config.apiUrl}/inspection/template`, inspection)
            .then((_) => this.props.onClose(false, true))
            .catch((_) => this.setState({ isLoading: false }));
    }

    createInspectionTemplate() {
        const { inspection } = this.state;
        axios
            .post(`${config.apiUrl}/inspection/template`, inspection)
            .then((_) => this.props.onClose(true, false))
            .catch((_) => this.setState({ isLoading: false }));
    }

    getValue(name: string) {
        // @ts-ignore
        return this.state.inspection[name];
    }

    handleChange(event: React.FormEvent<HTMLInputElement> | React.FormEvent<HTMLTextAreaElement>) {
        const { name, value } = event.currentTarget;

        // @ts-ignore
        const updatedInspection = { ...this.state.inspection, [name]: value };
        this.setState({ inspection: updatedInspection });
    }

    handleUpdate() {
        this.setState({ isLoading: true, isSubmitting: true });
        // check if any of the inputs are left empty
        const { inspection } = this.state;
        if (inspection.name === '' || inspection.description === '') {
            this.setState({ isLoading: false });
            return;
        }

        if (inspection.groups.length === 0) {
            this.setState({ isLoading: false });
            return;
        }

        for (const group of inspection.groups) {
            if (group.name === '') {
                this.setState({ isLoading: false });
                return;
            }

            if (group.tasks.length === 0) {
                this.setState({ isLoading: false });
                return;
            }

            for (const task of group.tasks) {
                if (task.description === '') {
                    this.setState({ isLoading: false });
                    return;
                }
            }
        }

        if (this.state.modalState === ModalStateType.Create) {
            this.createInspectionTemplate();
        } else {
            this.updateInspectionTemplate(this.state.inspection);
        }
    }

    addGroupTask(groupIndex: number) {
        const inspectionCopy = this.state.inspection;
        inspectionCopy.groups[groupIndex].tasks = [
            ...inspectionCopy.groups[groupIndex].tasks,
            { description: '' } as InspectionTemplateTask,
        ];
        this.setState({
            inspection: { ...inspectionCopy },
        });

        // const inspectionCopy = this.state.inspection;
        // inspectionCopy.inspectionGroups = [...inspectionCopy.inspectionGroups, { description: '' } as InspectionTemplateTask];
        // this.setState({
        //     inspection: { ...inspectionCopy },
        // });

        // const inspectionCopy = this.state.inspection;
        // inspectionCopy.inspectionTemplateItems = [
        //     ...inspectionCopy.inspectionTemplateItems,
        //     { description: '' } as InspectionTemplateTask,
        // ];
        // this.setState({
        //     inspection: { ...inspectionCopy },
        // });
    }

    removeInspectionGroupTask(groupIndex: number, taskIndex: number) {
        const { inspection } = this.state;
        inspection.groups[groupIndex].tasks.splice(taskIndex, 1);
        this.setState({ inspection });
    }

    removeInspectionGroup(groupIndex: number) {
        const { inspection } = this.state;
        inspection.groups.splice(groupIndex, 1);
        this.setState({ inspection });
    }

    updateDescription(taskIndex: number, description: string) {
        const { inspection } = this.state;
        inspection.inspectionTemplateItems[taskIndex].description = description;
        this.setState({ inspection });
    }

    updateInspectionGroupName(groupIndex: number, name: string) {
        const { inspection } = this.state;
        inspection.groups[groupIndex].name = name;
        this.setState({ inspection });
    }

    addInspectionGroup() {
        const inspectionCopy = this.state.inspection;
        inspectionCopy.groups = [
            ...inspectionCopy.groups,
            {
                description: '',
                isExpanded: true,
                tasks: [{ description: '' }] as InspectionTemplateTask[],
            } as InspectionTemplateGroup,
        ];
        this.setState({
            inspection: { ...inspectionCopy },
        });
    }

    updateGroupTaskDescription(groupIndex: number, taskIndex: number, description: string, markAsTouched = false) {
        const { inspection } = this.state;
        inspection.groups[groupIndex].tasks[taskIndex].description = description;
        if (markAsTouched) {
            inspection.groups[groupIndex].tasks[taskIndex].wasTouched = true;
        }
        this.setState({ inspection });
    }

    handleOnDragEnd(result: any, inspectionGroupIndex: number) {
        console.log(result);
        if (!result.destination) {
            return;
        }

        const { inspection } = this.state;
        const [reorderedItem] = inspection.groups[inspectionGroupIndex].tasks.splice(result.source.index, 1);

        const sourceIndex = result.source.index;
        const destinationIndex = result.destination.index;

        if (sourceIndex < destinationIndex) {
            for (let i = sourceIndex; i < destinationIndex; i++) {
                inspection.groups[inspectionGroupIndex].tasks[i].order = i + 1;
            }
        } else {
            for (let i = destinationIndex; i < sourceIndex; i++) {
                inspection.groups[inspectionGroupIndex].tasks[i].order = i - 1;
            }
        }

        inspection.groups[inspectionGroupIndex].tasks.splice(result.destination.index, 0, reorderedItem);
        this.setState({ inspection });
    }

    render() {
        const { modalHeader, isLoading, modalState, inspection, isSubmitting } = this.state;
        return (
            <Modal
                headerText={modalHeader}
                isOpen={true}
                onClose={() => this.props.onClose(false, false)}
                isLoading={isLoading}
                isBackgroundWhite={false}
                isDeleteHidden={modalState !== ModalStateType.Edit}
                onSave={this.handleUpdate}
                saveText={modalState === ModalStateType.Edit ? 'Update' : 'Create'}
                customHeader={
                    <div className="bg-blue-600 text-white p-2 flex items-center justify-between w-full">
                        <div className="flex items-center">
                            <Settings size={20} className="mr-2" />
                            <h2 className="text-xl font-bold">Edit Inspection Template</h2>
                        </div>
                        <button
                            className="text-white hover:bg-blue-700 p-1 rounded"
                            onClick={() => this.props.onClose(false, false)}
                        >
                            <X size={24} />
                        </button>
                    </div>
                }
                bodyClassName="bg-gray-100"
            >
                <div className="w-full">
                    <div className="pb-2">
                        <label className="font-semibold">Inspection Title</label>
                        <input
                            type="text"
                            name="name"
                            className={`rounded-md border w-full px-2 text-lg font-semibold py-2 focus:ring-1 focus:outline-none focus:border-blue-500 ${
                                isSubmitting && !this.getValue('name') ? ' border-red-500' : ''
                            }`}
                            value={this.getValue('name')}
                            autoFocus
                            onChange={this.handleChange}
                        />
                    </div>

                    <div className="pb-2">
                        <label className="font-semibold">Description</label>
                        <textarea
                            name="description"
                            className={`rounded-md border w-full px-2 text-lg font-semibold py-2 focus:ring-1 focus:outline-none focus:border-blue-500`}
                            value={this.getValue('description')}
                            autoFocus
                            onChange={this.handleChange}
                        />
                    </div>
                    {/* <div className="mb-4">
                        <Toggle
                            onChange={(newValue) =>
                                this.setState({
                                    inspection: { ...inspection, isDefault: newValue },
                                })
                            }
                            value={this.getValue('isDefault')}
                            name="isDefault"
                            label={'Is Default Inspection'}
                            disabled={false}
                        />
                        <div className="flex mt-0.5">
                            <BsQuestionCircle size={16} className={`text-blue-600 mr-1.5 hover:text-blue-800`} />
                            <small className="text-gray-500 ">
                                If this is checked, this inspection will be automatically added for all new ROs.
                            </small>
                        </div>
                    </div> */}

                    <div className="flex items-center my-4">
                        <div className="flex  mr-2 align-middle select-none">
                            <div className="w-fit mr-2">
                                <Toggle
                                    onChange={(newValue) =>
                                        this.setState({
                                            inspection: { ...inspection, isDefault: newValue },
                                        })
                                    }
                                    value={this.getValue('isDefault')}
                                    name="isDefault"
                                    disabled={false}
                                />
                            </div>
                            {/* <label
                                htmlFor="isDefault"
                                className={`block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer ${
                                    this.getValue('isDefault') ? 'bg-blue-500' : ''
                                }`}
                            >
                                <span
                                    className={`block h-6 w-6 rounded-full bg-white shadow transform transition-transform ${
                                        this.getValue('isDefault') ? 'translate-x-4' : 'translate-x-0'
                                    }`}
                                ></span>
                            </label> */}
                            <div>
                                <label className="text-sm font-semibold text-gray-700 text-nowrap">
                                    Default Inspection
                                </label>

                                <div className=" text-gray-500 text-sm flex items-center">
                                    <Info size={14} className="mr-1" />
                                    <span>
                                        If checked, this inspection will be automatically added for all new ROs.
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div>
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">Inspection Groups</h3>

                        {inspection?.groups?.map((group, inspectionGroupIndex) => {
                            return (
                                <div key={inspectionGroupIndex}>
                                    <div
                                        // key={inspectionGroupIndex}
                                        className="mb-4 border border-gray-200 rounded-lg overflow-hidden bg-white"
                                    >
                                        {/* Group Header */}
                                        <div className="bg-gray-50 p-3 border-b border-gray-200 flex items-center">
                                            <div className="flex items-center text-gray-500 cursor-move mr-2">
                                                <GripVertical size={16} />
                                            </div>

                                            <input
                                                type="text"
                                                name="name"
                                                className={`w-full b rounded-md border px-2 text-lg font-semibold py-2 focus:ring-1 focus:outline-none focus:border-blue-500 ${
                                                    isSubmitting && !group.name ? ' border-red-500' : ''
                                                }`}
                                                value={group.name}
                                                onChange={(e: any) =>
                                                    this.updateInspectionGroupName(inspectionGroupIndex, e.target.value)
                                                }
                                                autoFocus
                                                placeholder="Enter inspection group title"
                                            />

                                            <div className="flex items-center ml-2">
                                                <button
                                                    className="p-1 text-gray-500 hover:text-red-600"
                                                    title="Delete Group"
                                                    onClick={() => this.removeInspectionGroup(inspectionGroupIndex)}
                                                >
                                                    <Trash2 size={18} />
                                                </button>
                                                <button
                                                    className="p-1 text-gray-500 hover:text-blue-600 ml-1"
                                                    onClick={() => {
                                                        const inspectionCopy = this.state.inspection;
                                                        inspectionCopy.groups[inspectionGroupIndex].isExpanded =
                                                            !group.isExpanded;
                                                        this.setState({
                                                            inspection: { ...inspectionCopy },
                                                        });
                                                    }}
                                                >
                                                    {group.isExpanded ? (
                                                        <ChevronUp size={18} />
                                                    ) : (
                                                        <ChevronDown size={18} />
                                                    )}
                                                </button>
                                            </div>
                                        </div>

                                        {/* ITEMS */}
                                        {group.isExpanded && (
                                            <div className="p-3">
                                                <h4 className="text-sm font-semibold text-gray-700 mb-2">
                                                    Inspection Group Tasks
                                                </h4>
                                                <div className="space-y-2 mb-3">
                                                    <DragDropContext
                                                        onDragEnd={(res: any) =>
                                                            this.handleOnDragEnd(res, inspectionGroupIndex)
                                                        }
                                                    >
                                                        <Droppable droppableId={'droppable' + inspectionGroupIndex}>
                                                            {(provided) => (
                                                                <div
                                                                    {...provided.droppableProps}
                                                                    ref={provided.innerRef}
                                                                    style={{ listStyleType: 'none', padding: 0 }}
                                                                >
                                                                    {group?.tasks?.map((task, groupTaskIndex) => {
                                                                        return (
                                                                            <Draggable
                                                                                key={task.id}
                                                                                draggableId={
                                                                                    task.id || groupTaskIndex.toString()
                                                                                }
                                                                                index={groupTaskIndex}
                                                                            >
                                                                                {(provided) => (
                                                                                    <div
                                                                                        key={groupTaskIndex}
                                                                                        className="pb-2 flex w-full"
                                                                                        ref={provided.innerRef}
                                                                                        {...provided.draggableProps}
                                                                                        style={{
                                                                                            ...provided.draggableProps
                                                                                                .style,
                                                                                        }}
                                                                                    >
                                                                                        <div
                                                                                            {...provided.dragHandleProps}
                                                                                        >
                                                                                            <MdDragIndicator className="inline self-center mr-2 mt-2" />
                                                                                        </div>
                                                                                        <div className="w-full">
                                                                                            <input
                                                                                                type="text"
                                                                                                name="name"
                                                                                                className={`w-full  rounded-md border px-2 text-lg font-semibold py-2 focus:ring-1 focus:outline-none focus:border-blue-500 ${
                                                                                                    task.wasTouched &&
                                                                                                    isSubmitting &&
                                                                                                    task.description ===
                                                                                                        ''
                                                                                                        ? 'border-red-500'
                                                                                                        : ''
                                                                                                }`}
                                                                                                value={task.description}
                                                                                                onChange={(e: any) =>
                                                                                                    this.updateGroupTaskDescription(
                                                                                                        inspectionGroupIndex,
                                                                                                        groupTaskIndex,
                                                                                                        e.target.value,
                                                                                                    )
                                                                                                }
                                                                                                onBlur={(e: any) =>
                                                                                                    this.updateGroupTaskDescription(
                                                                                                        inspectionGroupIndex,
                                                                                                        groupTaskIndex,
                                                                                                        e.target.value,
                                                                                                        true,
                                                                                                    )
                                                                                                }
                                                                                                // autoFocus
                                                                                            />
                                                                                            {task.wasTouched &&
                                                                                            isSubmitting &&
                                                                                            !task.description ? (
                                                                                                <div className="text-red-500 text-sm">
                                                                                                    This field is
                                                                                                    required
                                                                                                </div>
                                                                                            ) : null}
                                                                                        </div>

                                                                                        <button
                                                                                            className="p-1 text-gray-500 hover:text-red-600"
                                                                                            title="Delete Group Task"
                                                                                            onClick={() =>
                                                                                                this.removeInspectionGroupTask(
                                                                                                    inspectionGroupIndex,
                                                                                                    groupTaskIndex,
                                                                                                )
                                                                                            }
                                                                                        >
                                                                                            <Trash2 size={18} />
                                                                                        </button>
                                                                                    </div>
                                                                                )}
                                                                            </Draggable>
                                                                        );
                                                                    })}
                                                                    {provided.placeholder}
                                                                </div>
                                                            )}
                                                        </Droppable>
                                                    </DragDropContext>

                                                    <button
                                                        className="flex items-center text-blue-600 hover:text-blue-800 text-sm font-semibold"
                                                        onClick={() => this.addGroupTask(inspectionGroupIndex)}
                                                    >
                                                        <Plus size={16} className="mr-1" />
                                                        <span>ADD TASK</span>
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                        <button
                            className="flex items-center justify-center w-full p-3 border border-dashed border-gray-300 rounded-lg text-blue-600 hover:bg-blue-50 font-semibold"
                            onClick={this.addInspectionGroup}
                        >
                            <Plus size={18} className="mr-2" />
                            <span>ADD INSPECTION GROUP</span>
                        </button>
                    </div>
                </div>
            </Modal>
        );
    }
}

export default connect()(InspectionModal);
